<template>
  <div>
    <div class="fixed-heading">
      <div class="card__general side-panel__header">
        <div class="card__title side-panel__announcement-card__title side-panel__announcement-header-card__title">
          {{ isEditMode ? $t('edit_challenge_panel_title', { challenge: challengeLabel }) : $t('add_challenge_panel_title_v2', { challenge: challengeLabel }) }}
        </div>
        <!-- Only while editing: there is nothing to delete yet on a challenge being created. -->
        <DsButton
          v-if="isEditMode"
          icon="trash"
          variant="minimal"
          size="small"
          class="side-panel__announcement-header-icon"
          style="margin-left: auto"
          v-tooltip.bottom="$t('delete')"
          @click="remove"
        />
        <DsButton
          icon="remove"
          variant="minimal"
          size="small"
          class="side-panel__announcement-header-icon"
          :style="isEditMode ? null : 'margin-left: auto'"
          @click="close"
        />
      </div>
    </div>

    <div class="scrollable side-panel__scrollable__content has-padding">
      <!--
        Four sections in the order the challenge is read: who is asking, what they are asking,
        what it is about, and the practical details. It used to be one undivided column of
        seventeen fields with two uppercase captions floating in it.
      -->
      <form class="mc-form grid gap-8 pb-24">

        <!-- ── Who is asking ── -->
        <section class="grid gap-1">
          <form-group :label="$t('add_announcement_title')" :errors="errors.title" required>
            <ds-input v-model="challenge.title" is-simplified/>
          </form-group>

          <form-group :label="$t('challenges_manage_panel_for_which_company')">
            <ActorSearch
              v-if="isMember || isOwner"
              :return-sql-id="true"
              :model-value="challenge.actor"
              @set-actor="handleActorChange($event, 'add')"
              @remove-actor="handleActorChange($event, 'remove')"
            />
            <!-- Everyone else picks from the actors they have claimed. -->
            <Dropdown
              v-else
              v-model="challenge.actor"
              :options="actorOptions"
              is-simplified
              @update:model-value="handleActorChange($event, 'add')"
            />
          </form-group>

          <form-group :label="$t('challenges_manage_panel_who_to_contact')" v-if="challenge.actor">
            <Dropdown
              v-if="!isLoadingUserSuggestions"
              :model-value="challenge.contact"
              :placeholder="$t('challenge_manage_contact_placeholder')"
              :options="contactOptions"
              is-simplified
              search
              allow-clear
              @update:model-value="handleContactChange"
            />
            <Checkbox
              v-model="challenge.isAnonymous"
              :label="$t('challenge_manage_anonymize')"
              is-simplified
            />
          </form-group>
        </section>

        <!-- ── What they are asking ── -->
        <section class="grid gap-1">
          <h3 class="mc-section">{{ $t('manage_challenge_subtitle_description') }}</h3>

          <form-group
            :label="$t('manage_challenge_description', { challenge: challengeLabel })"
            :errors="errors.description"
            required
          >
            <rich-text-area v-model="challenge.description" :minimal="true"/>
            <GenerativeTextArea
              class="mt-2"
              :feedback-type="GenerativeUserFeedbackType.challengeDescription"
              :enable-feedback="false"
              :loading="generating.description"
              :model-value="challenge.description"
              :enable-text-area="false"
              :disabled="!challenge.title"
              @generate="generate('description', $event)"
              @reGenerate="regenerate('description', $event)"
            />
          </form-group>

          <form-group
            :label="$t('challenge_background_of_the_challenge', { challenge: challengeLabel })"
            :errors="errors.background"
          >
            <rich-text-area v-model="challenge.background" :minimal="true"/>
            <GenerativeTextArea
              class="mt-2"
              :feedback-type="GenerativeUserFeedbackType.challengeBackground"
              :enable-feedback="false"
              :loading="generating.background"
              :model-value="challenge.background"
              :enable-text-area="false"
              :disabled="!canGenerateFromDescription"
              @generate="generate('background', $event)"
              @reGenerate="regenerate('background', $event)"
            />
          </form-group>

          <form-group :label="$t('manage_challenge_goal')" :errors="errors.objective" required>
            <rich-text-area v-model="challenge.objective" :minimal="true"/>
            <GenerativeTextArea
              class="mt-2"
              :feedback-type="GenerativeUserFeedbackType.challengeObjective"
              :enable-feedback="false"
              :loading="generating.objective"
              :model-value="challenge.objective"
              :enable-text-area="false"
              :disabled="!canGenerateFromDescription"
              @generate="generate('objective', $event)"
              @reGenerate="regenerate('objective', $event)"
            />
          </form-group>
        </section>

        <!-- ── What it is about ── -->
        <section class="grid gap-1">
          <h3 class="mc-section">{{ $t('challenges_goals') }}</h3>
          <TaxonomyQuickEditChallenges
            :model="challenge"
            :errors="errors.goals"
            @update:taxonomies="applyGoals"
          />
        </section>

        <!-- ── The practical details ── -->
        <section class="grid gap-1">
          <h3 class="mc-section">{{ $t('manage_challenge_subtitle_requirements') }}</h3>

          <!--
            The Sustainable Development Goals, as a picker you can read the state of: the ones
            that are on are full colour with a check on them, the rest are dimmed. A 4px tint
            around a full-colour square — what this was — is not a state anybody can see.
          -->
          <form-group :label="$t('add_challenge_sdg')" :errors="errors.sdgs">
            <div class="grid grid-cols-[repeat(auto-fill,minmax(58px,1fr))] gap-2">
              <button
                class="relative block w-full overflow-hidden rounded-lg border-2 p-0 cursor-pointer transition-all"
                type="button"
                v-for="sdg of sdgChoices"
                :key="'sdg' + sdg.value"
                :class="isSdgSelected(sdg)
                  ? 'border-primary shadow-[0_0_0_3px_var(--primary-community-lightest)]'
                  : 'border-transparent opacity-40 hover:opacity-80'"
                :aria-pressed="isSdgSelected(sdg) ? 'true' : 'false'"
                :aria-label="sdg.title"
                v-tooltip.top="sdg.title"
                @click="toggleSdg(sdg)"
              >
                <!-- The artwork is square, so it needs no aspect utility — and it could not have
                     one: @tailwindcss/aspect-ratio takes the core `aspect-*` plugin's place. -->
                <img class="block w-full h-auto" :src="sdg.image" :alt="sdg.title" loading="lazy">
                <span
                  class="mc-check absolute right-1 top-1 tw-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white"
                  v-if="isSdgSelected(sdg)"
                  aria-hidden="true"
                >
                  <Icon name="check" size="12"/>
                </span>
              </button>
            </div>
            <p class="m-0 mt-1.5 text-xs text-slate-500" v-if="selectedSdgs.length">
              {{ $t('challenges_filters_selected', { count: selectedSdgs.length }) }}
            </p>
          </form-group>

          <!-- Two per row: these are all short values, and the panel is 650px wide. -->
          <div class="grid gap-x-5 gap-y-1 sm:grid-cols-2">
            <form-group
              :label="$t('add_challenge_invite_only')"
              :errors="errors.visibility"
              v-if="isOwner"
            >
              <div class="tw-flex flex-wrap gap-2">
                <DsButton
                  v-for="option of visibilityOptions"
                  :key="'visibility' + option.value"
                  :label="option.label"
                  :variant="challenge.visibility === option.value ? 'rounded-active' : 'rounded-secondary'"
                  @click="challenge.visibility = option.value"
                />
              </div>
            </form-group>

            <form-group :label="$t('add_challenge_status')" :errors="errors.status" v-if="isOwner">
              <dropdown v-model="challenge.status" :options="statusOptions" is-simplified/>
            </form-group>

            <form-group
              :label="$t('add_challenge_end_date')"
              :info-text="$t('challenge_end_date_info', { challenge: challengeLabel })"
            >
              <datepicker
                :model-value="challenge.endDate"
                :allow-reset="true"
                is-simplified
                @update:modelValue="challenge.endDate = $event"
              />
            </form-group>

            <form-group
              :label="$t('add_challenge_geography_scope')"
              :info-text="$t('challenge_geoscope_info')"
            >
              <dropdown
                v-model="challenge.geographicalScope"
                :options="geographyOptions"
                is-simplified
                search
                multiple
              />
            </form-group>

            <form-group :label="$t('add_challenge_link_to_communities')" :errors="errors.communityIds">
              <dropdown
                v-model="challenge.communityIds"
                :options="communityOptions"
                :disabled="!isMember"
                is-simplified
                multiple
                allow-clear
              />
            </form-group>

            <form-group
              :label="$t('add_announcement_tags')"
              :errors="errors.tags"
              :info-text="$t('challenge_tags_info', { challenges: challengesLabel })"
            >
              <AutocompleteTagInput
                :placeholder="$t('add_announcement_tags_placeholder')"
                :tags="challenge.tags"
                :options="tagOptions"
                is-simplified
                @tagsChanged="updateTags"
                @tagChanged="handleTagChanged"
              />
            </form-group>
          </div>

          <form-group
            :label="$t('add_announcement_image')"
            :errors="errors.image"
            :info-text="$t('add_challenge_image_help_text', { challenge: challengeLabel })"
          >
            <image-input
              v-model="challenge.originalImage"
              width="200"
              height="100"
              @update:modelValue="challenge.image = $event"
            />
          </form-group>
        </section>
      </form>
    </div>

    <action-bar editing="1" class="side-panel__announcement-bottom-section">
      <DsButton
        variant="rounded"
        :loading="saving"
        :label="saveButtonLabel"
        :disabled="!!missingRequirements.length || saving"
        @click="save"
      />
      <!-- Why the button is off. It used to just sit there, disabled, with nothing to explain it. -->
      <span class="action-bar__message text-xs text-slate-500" v-if="missingRequirements.length">
        {{ $t('challenge_manage_missing', { fields: missingRequirements.join(', ') }) }}
      </span>
    </action-bar>
  </div>
</template>

<script lang="ts">
  import { Challenges } from '@/Domain/Challenge/Api/challenges'
  import { fetchSdgs } from '@/api/taxonomies'
  import { searchContacts } from '@/api/actors'
  import { generateText } from '@/api/generative'
  import { trackHeapEvent } from '@/util/analytics'
  import { htmlText } from '@/util/helpers'

  import TagsMixin from '@/util/TagsMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UserMixin from '@/util/UserMixin'

  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { ACTION_TYPES as CHALLENGES_ACTION_TYPES } from '@/store/modules/challenges'
  import { GenerativeType, GenerativeUserFeedbackType } from '@/Domain/Challenge/enums'
  import { sustainabilityGoals, sustainabilityGoalsNL } from '@/constants/config'
  import { CONTINENTS, COUNTRIES } from '@/constants/countries'
  import MODAL_IDS from '@/constants/modal-ids'
  import { Challenge } from '@/Domain/Challenge/Types/challenge-types'

  import ActionBar from '@/components/Form/ActionBar.vue'
  import ActorSearch from '@/components/ActorSearch/ActorSearch.vue'
  import AutocompleteTagInput from '@/components/Form/AutocompleteTagInput.vue'
  import Checkbox from '@/components/Form/Checkbox.vue'
  import Datepicker from '@/components/Form/Datepicker.vue'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import GenerativeTextArea from '@/components/TextArea/GenerativeTextArea.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import ImageInput from '@/components/Form/ImageInput.vue'
  import RichTextArea from '@/components/TextArea/RichTextArea.vue'
  import TaxonomyQuickEditChallenges from '@/Domain/Challenge/Components/TaxonomyQuickEditChallenges.vue'

  /** The five taxonomies that make up a challenge's goals — see TaxonomyQuickEditChallenges. */
  const GOAL_FIELDS = ['activities', 'industries', 'technology', 'domains', 'stages']

  /** The long-form fields, and the generator that writes each one. */
  const GENERATIVE_FIELDS = {
    description: GenerativeType.challengeDescription,
    background: GenerativeType.challengeBackground,
    objective: GenerativeType.challengeObjective,
  }

  function emptyChallenge (): Challenge {
    return {
      title: '',
      actor: null,
      description: '',
      background: '',
      status: 'new',
      image: null,
      tags: [],
      objective: '',
      endDate: '',
      visibility: '',
      geographicalScope: [],
      sdgs: [],
      communityIds: [],
      goals: [],
      isAnonymous: false,
      activities: [],
      industries: [],
      technology: [],
      domains: [],
      stages: [],
      contact: null,
    } as Challenge
  }

  export default {
    name: 'ManageChallengePanel',
    mixins: [TagsMixin, TranslationsMixin, UserMixin],
    components: {
      ActionBar,
      ActorSearch,
      AutocompleteTagInput,
      Checkbox,
      Datepicker,
      Dropdown,
      FormGroup,
      GenerativeTextArea,
      Icon,
      ImageInput,
      RichTextArea,
      TaxonomyQuickEditChallenges,
    },
    props: {
      challengeData: {
        type: Object as () => Challenge | null,
        default: null,
      },
    },
    data () {
      return {
        challenge: emptyChallenge(),
        displayedUserSuggestions: [],
        selectedContactOption: null,
        isLoadingUserSuggestions: false,
        generating: { description: false, background: false, objective: false },
        sdgOptions: [],
        saving: false,
        errors: {} as Record<string, any>,
      }
    },
    computed: {
      GenerativeUserFeedbackType () {
        return GenerativeUserFeedbackType
      },
      isEditMode (): boolean {
        return !!this.challenge.id
      },
      /**
       * What still stands between this challenge and being published. One list drives the disabled
       * save button, the message beside it, and the errors on the fields — they used to disagree:
       * the button wanted an objective and a goal, the validation asked for neither, and two
       * fields were marked required that nothing ever checked.
       */
      missingRequirements (): string[] {
        const missing = []

        if (!this.challenge.title) {
          missing.push(this.$t('add_announcement_title'))
        }

        if (!this.hasText(this.challenge.description)) {
          missing.push(this.$t('manage_challenge_subtitle_description'))
        }

        if (!this.hasText(this.challenge.objective)) {
          missing.push(this.$t('manage_challenge_goal'))
        }

        if (!GOAL_FIELDS.some(field => (this.challenge[field] || []).length)) {
          missing.push(this.$t('challenges_goals'))
        }

        return missing
      },
      /** The background and the objective are written from the description, so it has to exist. */
      canGenerateFromDescription (): boolean {
        return !!this.challenge.title && this.hasText(this.challenge.description)
      },
      saveButtonLabel (): string {
        if (!this.isEditMode) {
          return this.$t('add_challenge_save')
        }

        if (this.challenge.curation === 'in_progress' && (this.isMember || (this.isPortfolioMember && this.$route.name === 'datalab'))) {
          return this.$t('challenge_manage_approve_and_update')
        }

        return this.$t('edit_challenge_button', { challenge: this.challengeLabel })
      },
      /**
       * The contributors to choose from, with the challenge's current contact guaranteed to be
       * among them.
       *
       * /api/actors/:id/contacts only returns users who hold an `actor_claims` row for this actor
       * *and* a member/owner/portfolio/actor role, so a contact set before either of those was
       * true is simply absent from it. Dropdown falls back to `{ label: value }` for a value it
       * cannot resolve to an option — which is why the field was showing a bare user id.
       */
      contactOptions () {
        const options = [...this.displayedUserSuggestions]
        const selected = this.selectedContactOption

        if (selected && !options.some(option => option.value === selected.value)) {
          options.unshift(selected)
        }

        return options
      },
      communityId () {
        return this.$route.params.communityId
      },
      communityOptions () {
        return this.$store.state.communities.listData.data.map(community => ({
          label: community.name,
          value: community.id,
        }))
      },
      actorOptions () {
        return (this.claimedActors || []).map(actor => ({
          value: actor.id,
          label: actor.name,
        }))
      },
      visibilityOptions () {
        return [
          { label: this.$t('challenges_public'), value: 'public' },
          { label: this.$t('challenges_private'), value: 'private' },
        ]
      },
      statusOptions () {
        return ['new', 'open', 'solved'].map(status => ({
          label: this.$t('challenges_' + status),
          value: status,
        }))
      },
      geographyOptions () {
        const geographies = { ...CONTINENTS, ...COUNTRIES }

        return Object.keys(geographies).map(key => ({
          label: geographies[key],
          value: key,
        }))
      },
      translatedSustainabilityGoals () {
        return this.$store.getters.activeLocale === 'nl' ? sustainabilityGoalsNL : sustainabilityGoals
      },
      sdgImagesPath () {
        return this.$store.getters.activeLocale === 'nl' ? '/images/SDG_nl/' : '/images/SDG/'
      },
      /**
       * The goals with their artwork. The number comes out of the name ("SDG 7: …") rather than
       * out of the position in the response, which only lines up while the ecosystem holds all
       * seventeen in order.
       */
      sdgChoices () {
        const labels = this.translatedSustainabilityGoals

        return this.sdgOptions.map((sdg, index) => {
          const parsed = Number(String(sdg.label).replace(/^\D*(\d+).*$/, '$1'))
          const number = parsed >= 1 && parsed <= labels.length ? parsed : index + 1

          return {
            value: sdg.value,
            label: sdg.label,
            title: labels[number - 1] || sdg.label,
            image: `${this.sdgImagesPath}${number}.jpg`,
          }
        })
      },
      selectedSdgs () {
        return this.challenge.sdgs || []
      },
    },
    methods: {
      /** True for HTML that renders as something — an empty `<p></p>` does not. */
      hasText (html): boolean {
        if (!html) {
          return false
        }

        return htmlText(html).trim().length > 0
      },

      /* --- fields ------------------------------------------------------ */

      applyGoals (taxonomies) {
        GOAL_FIELDS.forEach(field => {
          this.challenge[field] = taxonomies[field] || []
        })
      },
      handleActorChange (actor, action: 'add' | 'remove') {
        this.challenge.actor = action === 'add' ? actor : null
        this.challenge.contact = null
        this.selectedContactOption = null

        if (action === 'add') {
          this.loadContactSuggestions()
        } else {
          this.displayedUserSuggestions = []
        }
      },
      /** Remembered as an option, so searching for somebody else cannot orphan the current pick. */
      handleContactChange (contactId) {
        this.challenge.contact = contactId
        this.selectedContactOption = this.contactOptions.find(option => option.value === contactId) || null
      },
      contactLabel (user): string {
        return user.email ? `${user.name} (${user.email})` : user.name
      },
      isSdgSelected (sdg): boolean {
        return this.selectedSdgs.some(selected => selected.value === sdg.value)
      },
      toggleSdg (sdg) {
        if (this.isSdgSelected(sdg)) {
          this.challenge.sdgs = this.selectedSdgs.filter(selected => selected.value !== sdg.value)
          return
        }

        // Stored the way the API returns them: the id and the goal's own name.
        this.challenge.sdgs = [...this.selectedSdgs, { value: sdg.value, label: sdg.label }]
      },
      updateTags (tags) {
        this.challenge.tags = tags
      },
      handleTagChanged (tag) {
        this.updateTagOptions(tag)
      },

      /* --- generated copy ---------------------------------------------- */

      generate (field: string, context) {
        this.runGenerator(field, context)
      },
      regenerate (field: string, context) {
        this.challenge.previousText = this.challenge[field]
        this.runGenerator(field, context)
      },
      runGenerator (field: string, context) {
        this.generating[field] = true

        generateText(GENERATIVE_FIELDS[field], this.challenge, context)
          .then(response => {
            this.challenge[field] = response.result
          })
          .finally(() => {
            this.generating[field] = false
          })
      },

      /* --- data -------------------------------------------------------- */

      loadSdgs () {
        return fetchSdgs()
          .then(response => {
            this.sdgOptions = response.map(sdg => ({ label: sdg.name, value: sdg.id }))
          })
      },
      loadContactSuggestions (query = '') {
        const actor = this.challenge.actor

        if (!actor) {
          this.displayedUserSuggestions = []
          return Promise.resolve()
        }

        const actorId = actor instanceof Object ? actor.id : actor

        return searchContacts(actorId, { query })
          .then(suggestions => {
            this.displayedUserSuggestions = suggestions.map(user => ({
              label: this.contactLabel(user),
              value: user.id,
            }))
          })
          .catch(() => {
            this.displayedUserSuggestions = []
          })
      },

      /* --- saving and deleting ----------------------------------------- */

      validateForm (): boolean {
        this.errors = {}

        if (!this.challenge.title) {
          this.errors.title = [this.$t('challenge_title_required')]
        }

        if (!this.hasText(this.challenge.description)) {
          this.errors.description = [this.$t('challenge_description_required')]
        }

        if (!this.hasText(this.challenge.objective)) {
          this.errors.objective = [this.$t('challenge_objective_required')]
        }

        if (!GOAL_FIELDS.some(field => (this.challenge[field] || []).length)) {
          this.errors.goals = [this.$t('challenge_goals_required', { challenge: this.challengeLabel })]
        }

        return Object.keys(this.errors).length === 0
      },
      async save () {
        if (this.saving || !this.validateForm()) {
          return
        }

        const wasEditing = this.isEditMode
        this.saving = true

        try {
          const challenge = await Challenges.post(this.challenge)

          this.$bus.emit(wasEditing ? 'challengeUpdated' : 'challengeCreated')

          const id = Number((challenge && challenge.id) || this.challenge.id)

          if (id) {
            this.$store.dispatch(CHALLENGES_ACTION_TYPES.FETCH_CHALLENGE_DETAIL, id)
          }

          this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
        } catch (errors) {
          this.errors = errors || {}
        } finally {
          this.saving = false
        }
      },
      remove () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: this.challenge.title,
          resource: this.challenge,
          modalContextType: 'challenge',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      async onDeleteConfirmed () {
        await Challenges.delete(this.challenge.id)

        this.$bus.emit('challengeDeleted')
        trackHeapEvent('manageChallenge.remove')
        this.close()

        if (this.communityId) {
          this.$router.push(`/communities/${this.communityId}/challenges`)
          return
        }

        this.$router.push('/challenges')
      },
      close () {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
      },
    },
    async mounted () {
      if (this.challengeData) {
        this.challenge = {
          ...emptyChallenge(),
          ...this.challengeData,
          originalImage: `/api/challenges/${this.challengeData.id}/image`,
        }

        if (this.challengeData.contact) {
          this.challenge.contact = this.challengeData.contact.id
          // The name is already on the challenge, so the field can label the contact without
          // waiting for — or depending on — the contributors request.
          this.selectedContactOption = {
            value: this.challengeData.contact.id,
            label: this.contactLabel(this.challengeData.contact),
          }
        }
      }

      if (this.communityId && !(this.challenge.communityIds || []).length) {
        this.challenge.communityIds = [Number(this.communityId)]
      }

      this.loadSdgs()

      if (this.challenge.actor) {
        this.isLoadingUserSuggestions = true
        await this.loadContactSuggestions()
        this.isLoadingUserSuggestions = false
      }
    },
    created () {
      // A named handler, so unregistering takes only this listener with it.
      this.$bus.on('challengeDeleteConfirmation', this.onDeleteConfirmed)
    },
    beforeUnmount () {
      this.$bus.off('challengeDeleteConfirmation', this.onDeleteConfirmed)
    },
  }
</script>

<style lang="scss" scoped>
  /* A quiet rule with a label on it, to separate the sections without shouting between them. */
  .mc-section {
    @apply mb-1 border-b border-gray-100 pb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400;
  }

  .mc-form {
    /* FormGroup's info-text icon inherits a fill that is invisible on white. */
    :deep(.svg-icon--information-outline) path {
      fill: #555;
    }
  }

  /**
   * The tick on a selected goal. `check` hardcodes `fill="#515151"` on its path, which beats the
   * `fill: currentColor` the Icon component puts on the container — so without this it is a grey
   * tick on a brand-coloured disc.
   */
  .mc-check :deep(.svg-icon path) {
    fill: currentColor !important;
  }
</style>
